/**
 * VO annotation chrome — single authority for living proposal line cards,
 * system overview UPDATED badges, and related meta lines.
 */

import type { ScopeAnnotation } from "../variationOrderCurrentScope";
import type { VoSystemOverviewAnnotation } from "../variationOrderSystemOverview";
import { voViewHref } from "../variationOrderPresentation";

export type VoLiveAnnotationKind = "added" | "removed" | "quantity_changed";

export type VoLiveAnnotation = ScopeAnnotation & {
  kind: VoLiveAnnotationKind;
  replacementRole?: "replacement" | "replaced" | null;
};

export type VoAnnotationStatusBadge =
  | "ADDED"
  | "REMOVED"
  | "UPDATED"
  | "REPLACEMENT"
  | "REPLACED";

export type VoLineAnnotationPresentation = {
  statusBadge: VoAnnotationStatusBadge | null;
  badge: string | null;
  metaLine: string | null;
  changeReason: string | null;
};

export type VoOverviewAnnotationPresentation = {
  statusBadge: "UPDATED";
  badge: string;
  metaLine: string | null;
  viewHref: string | null;
};

export function renderVoAnnotationStatusBadge(
  ann: VoLiveAnnotation | undefined,
): VoAnnotationStatusBadge | null {
  if (!ann) return null;
  if (ann.replacementRole === "replacement") return "REPLACEMENT";
  if (ann.replacementRole === "replaced") return "REPLACED";
  if (ann.kind === "added") return "ADDED";
  if (ann.kind === "removed") return "REMOVED";
  return "UPDATED";
}

export function renderVoAnnotationBadgeText(ann: VoLiveAnnotation | undefined): string | null {
  if (!ann) return null;
  if (ann.replacementRole === "replacement") return `Replacement by ${ann.voNumber}`;
  if (ann.replacementRole === "replaced") return `Replaced by ${ann.voNumber}`;
  if (ann.kind === "added") return `Added by ${ann.voNumber}`;
  if (ann.kind === "removed") return `Removed by ${ann.voNumber}`;
  return `Updated by ${ann.voNumber}`;
}

export function renderVoAnnotationDate(date: string | Date | null | undefined): string | null {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });
}

export function renderVoAnnotationChangeReason(ann: VoLiveAnnotation | undefined): string | null {
  // Prefer reason once under the active replacement product.
  if (ann?.replacementRole === "replaced") return null;
  const reason = String(ann?.changeReason ?? "").trim();
  return reason || null;
}

/** Date (+ optional Previously Qty N) joined with ` · ` — living proposal line meta. */
export function renderVoAnnotationMetaLine(ann: VoLiveAnnotation | undefined): string | null {
  const dateLabel = renderVoAnnotationDate(ann?.date);
  const previouslyQty =
    ann?.kind === "quantity_changed" && ann.quantityBefore != null
      ? `Previously Qty ${ann.quantityBefore}`
      : null;
  const parts = [dateLabel, previouslyQty].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

export function renderVoLineAnnotation(
  ann: VoLiveAnnotation | undefined,
): VoLineAnnotationPresentation {
  return {
    statusBadge: renderVoAnnotationStatusBadge(ann),
    badge: renderVoAnnotationBadgeText(ann),
    metaLine: renderVoAnnotationMetaLine(ann),
    changeReason: renderVoAnnotationChangeReason(ann),
  };
}

export function renderVoOverviewAnnotation(
  ann: VoSystemOverviewAnnotation | null | undefined,
  opts?: { staffLinks?: boolean },
): VoOverviewAnnotationPresentation | null {
  if (!ann) return null;
  return {
    statusBadge: "UPDATED",
    badge: `Updated by ${ann.voNumber}`,
    metaLine: renderVoAnnotationDate(ann.date),
    viewHref: voViewHref({
      staffLinks: opts?.staffLinks === true,
      voId: ann.voId,
      publicToken: ann.publicToken,
    }),
  };
}

export function renderVoQuantityDisplay(input: {
  quantity: number | string;
  ann?: VoLiveAnnotation | undefined;
}): number | string {
  const ann = input.ann;
  if (ann?.kind === "quantity_changed" && ann.quantityBefore != null) {
    return `${ann.quantityBefore} → ${ann.quantityAfter ?? input.quantity}`;
  }
  return input.quantity;
}
